import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  Spinner,
  Badge,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate  } from 'react-router-dom'

import { getLeadById } from '../../../../redux/actions/lead';
import LeadEditForm from '../LeadEditForm';
import {
  SOURCE_OPTIONS,
  STATE_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
} from '../../constants';
import { getItem } from '../../../../redux/actions/forms';
import {
  datetimeFormat,
  dateFormat,
  priceFormat,
  currencyFormat,
} from '../../../../utils/utils';
import styles from './styles.module.scss';

function LeadDetail({ leadId, onSubmit, onCancel }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const leadDetailState = useSelector((state) => state.lead.getById);
  const formDetailState = useSelector((state) => state.forms.getItem);
  const [modal, setModal] = useState(true);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);

  const isAdmin = localStorage.getItem('role') === 'admin';

  const toggle = (operation) => {
    setModal(!modal);
    if (operation === 'submit') {
      onSubmit();
    } else {
      onCancel();
    }
  };

  const handleClientDetail = (clientId) => {
    navigate(`/client-detail/${clientId}`);
  };

  const handleHoldingDetail = (holdingId) => {
    dispatch(getItem(holdingId));
  };

  const getStateLabel = (stateValue) => {
    const stateOption = STATE_OPTIONS.find(
      (option) => option.value === stateValue,
    );
    return stateOption ? stateOption.label : stateValue;
  };

  const sourceToText = (source) => {
    const sourceOption = SOURCE_OPTIONS.find(
      (option) => option.value === source,
    );
    return sourceOption ? sourceOption.label : 'No especificada';
  };

  const contactMethodToText = (contactMethod) => {
    const contactMethodOption = CONTACT_METHOD_OPTIONS.find(
      (option) => option.value === contactMethod,
    );
    return contactMethodOption ? contactMethodOption.label : 'No especificada';
  };

  const paymentMethodToText = (paymentMethod) => {
    const paymentMethodOption = PAYMENT_METHOD_OPTIONS.find(
      (option) => option.value === paymentMethod,
    );
    return paymentMethodOption ? paymentMethodOption.label : 'No especificada';
  };

  useEffect(() => {
    if (leadId) {
      setLoading(true);
      setErrorMessage(null);
      dispatch(getLeadById(leadId));
    } else {
      setErrorMessage('No se proporcionó un ID de lead válido');
      setLoading(false);
    }
  }, [leadId, dispatch]);

  useEffect(() => {
    if (leadDetailState) {
      setLoading(false);
      if (leadDetailState.status === 'danger') {
        setErrorMessage(
          leadDetailState.message || 'Error al cargar los detalles del lead',
        );
      }
    }
  }, [leadDetailState]);

  useEffect(() => {
    if (formDetailState) {
      if (formDetailState.status === 'success') {
        navigate('/item-admin', { state: formDetailState.data.data, });
      }
    }
  }, [formDetailState]);

  const renderLeadInfo = () => {
    if (loading) {
      return (
        <div className="text-center p-5">
          <Spinner color="primary" />
          <p className="mt-3">Cargando detalles del lead...</p>
        </div>
      );
    }

    if (errorMessage) {
      return <Alert color="danger">{errorMessage}</Alert>;
    }

    if (!leadDetailState?.data) {
      return (
        <Alert color="warning">No se encontraron datos para este lead</Alert>
      );
    }

    const lead = leadDetailState.data.data;

    return (
      <article className={styles.container}>
        <section className={styles.baseInfo}>
          <section className={styles.clientInfo}>
            <p>
              <strong>Información del Cliente</strong>
              {lead.new?.source?.startsWith('whatsapp') && (
                <Badge color="success" className="ml-2">
                  WhatsApp · Sofía
                </Badge>
              )}
            </p>
            <p className="pl-2">
              <strong>Nombre:</strong>{' '}
              {lead.useCompanyData === true && lead.clientInfo?.companyInfo
                ? lead.clientInfo.companyInfo.name
                : `${lead.clientInfo?.firstName} ${lead.clientInfo?.lastName}`}
              <br />
              <strong>Correo electrónico:</strong> {lead.clientInfo?.email}
              <br />
              <strong>Teléfono:</strong> {lead.clientInfo?.phone}
              <br />
              <strong>RUT:</strong>{' '}
              {lead.useCompanyData === true && lead.clientInfo?.companyInfo
                ? lead.clientInfo.companyInfo.rut
                : lead.clientInfo?.rut}
            </p>
            <Button
              color="info"
              size="sm"
              onClick={() => handleClientDetail(lead.clientId)}
            >
              Ver detalle completo del cliente
            </Button>
          </section>
          <section className={styles.holdingInfo}>
            <p>
              <strong>Información del Bien</strong>
            </p>
            <p className="pl-2">
              <strong>Número Interno:</strong> {lead.holdingId}
              <br />
              <strong>Marca / Modelo:</strong> {lead.holdingInfo?.brand}
              {' / '}
              {lead.holdingInfo?.model}
              <br />
              <strong>Año:</strong> {lead.holdingInfo?.year}
              <br />
              <strong>Precio:</strong>{' '}
              <span className={styles.highlightPrice}>
                {priceFormat(
                  lead.holdingInfo?.price,
                  lead.holdingInfo?.iva,
                  lead.holdingInfo?.comission,
                )}
              </span>
            </p>
            <Button
              color="info"
              size="sm"
              onClick={() => handleHoldingDetail(lead.holdingId)}
            >
              Ver detalle completo del bien
            </Button>
          </section>
        </section>
        {(lead.conversationSummary || lead.negotiation?.handoffSummary) && (
          <section className="mb-3">
            <p>
              <strong>Resumen de la conversación (Sofía)</strong>
            </p>
            {lead.conversationSummary && (
              <p className="pl-2" style={{ whiteSpace: 'pre-line' }}>
                {lead.conversationSummary}
              </p>
            )}
            {lead.negotiation?.handoffSummary &&
              lead.negotiation.handoffSummary !== lead.conversationSummary && (
                <p className="pl-2" style={{ whiteSpace: 'pre-line' }}>
                  <em>Nota de derivación:</em> {lead.negotiation.handoffSummary}
                </p>
              )}
          </section>
        )}
        <hr />{' '}
        <section className="mb-3">
          <p>
            <strong>
              Información del Lead (Estado actual: {getStateLabel(lead.state)})
            </strong>
          </p>
          <section className={styles.baseDetail}>
            <section className={styles.colDetail}>
              <section className="mb-3">
                <h6 className="text-muted small font-weight-normal mb-1">
                  {getStateLabel('new')}
                </h6>
                <p>
                  <strong>Fuente:</strong> {sourceToText(lead.new?.source)}
                </p>
              </section>
            </section>
            <section className={styles.colDetail}>
              {lead.assigned && (
                <section className="mb-3">
                  <h6 className="text-muted small font-weight-normal mb-1">
                    {getStateLabel('assigned')}
                  </h6>
                  <p>
                    <strong>Nombre:</strong>{' '}
                    {lead.assigned?.name || 'No asignado'}
                    <br />
                    <strong>Correo electrónico:</strong>{' '}
                    {lead.assigned.userEmail || 'No asignado'}
                    <br />
                    <strong>Rol:</strong> {lead.assigned?.role || 'No asignado'}
                  </p>
                </section>
              )}
            </section>
          </section>
          <section className={styles.baseDetail}>
            <section className={styles.colDetail}>
              {lead.negotiation && (
                <section className="mb-3">
                  <h6 className="text-muted small font-weight-normal mb-1">
                    {getStateLabel('negotiation')}
                  </h6>
                  <p>
                    <strong>Método de contacto:</strong>{' '}
                    {contactMethodToText(lead.negotiation.contactMethod)}
                    {lead.negotiation.pdf && (
                      <>
                        <br />
                        <strong>Versión de la cotización:</strong>{' '}
                        {lead.negotiation.version || 'No asignado'}
                        <br />
                        <strong>PDF de la cotización:</strong>{' '}
                        <Button
                          color="primary"
                          size="sm"
                          className="ml-2"
                          onClick={() =>
                            window.open(lead.negotiation.pdf, '_blank')
                          }
                        >
                          <i className="fa fa-download mr-1"></i>
                          Descargar PDF
                        </Button>
                      </>
                    )}
                  </p>
                </section>
              )}
            </section>
            <section className={styles.colDetail}>
              {lead.visit && (
                <section className="mb-3">
                  <h6 className="text-muted small font-weight-normal mb-1">
                    {getStateLabel('visit')}
                  </h6>
                  <p>
                    <strong>Fecha y hora:</strong>{' '}
                    <span className={styles.highlightDate}>
                      {lead.visit.datetime
                        ? datetimeFormat(lead.visit.datetime)
                        : 'No asignado'}
                    </span>
                    <br />
                    <strong>¿Cliente asistió?</strong>{' '}
                    {lead.visit.isArrived ? 'Sí' : 'No'}
                    {lead.visit.isArrived && (
                      <>
                        <br />
                        <strong>Precio de la visita:</strong>{' '}
                        <span className={styles.highlightPrice}>
                          {priceFormat(lead.visit.price, lead.visit.iva)}
                        </span>
                        <br />
                        <strong>Método de pago:</strong>{' '}
                        {paymentMethodToText(lead.visit.paymentMethod)}
                        <br />
                        <strong>Observación:</strong>{' '}
                        {lead.visit.observation || 'No asignado'}
                      </>
                    )}
                    {lead.visit.isArrived && lead.visit.pdf && (
                      <>
                        <br />
                        <strong>Versión de la oferta:</strong>{' '}
                        {lead.visit.version || 'No asignado'}
                        <br />
                        <strong>PDF de la oferta:</strong>{' '}
                        <Button
                          color="primary"
                          size="sm"
                          className="ml-2"
                          onClick={() => window.open(lead.visit.pdf, '_blank')}
                        >
                          <i className="fa fa-download mr-1"></i>
                          Descargar PDF
                        </Button>
                      </>
                    )}
                  </p>
                </section>
              )}
            </section>
          </section>
          <section className={styles.baseDetail}>
            <section className={styles.colDetail}>
              {lead.close && (
                <section className="mb-3">
                  <h6 className="text-muted small font-weight-normal mb-1">
                    {getStateLabel('close')}
                  </h6>
                  <p>
                    <strong>Fecha de cierre:</strong>{' '}
                    <span className={styles.highlightDate}>
                      {lead.close.date
                        ? dateFormat(lead.close.date)
                        : 'No asignado'}
                    </span>
                    <br />
                    <strong>Comentario:</strong>{' '}
                    {lead.close.comment || 'No asignado'}
                  </p>
                </section>
              )}
            </section>
            <section className={styles.colDetail}>
              {lead.completed && (
                <section className="mb-3">
                  <h6 className="text-muted small font-weight-normal mb-1">
                    {getStateLabel('completed')}
                  </h6>
                  <p>
                    <strong>Fecha de pago:</strong>{' '}
                    <span className={styles.highlightDate}>
                      {lead.completed.date
                        ? dateFormat(lead.completed.date)
                        : 'No asignado'}
                    </span>
                    <br />
                    <strong>Precio:</strong>{' '}
                    <span className={styles.highlightPrice}>
                      {currencyFormat(lead.completed.price)}
                    </span>
                    <br />
                    <strong>Método de pago:</strong>{' '}
                    {paymentMethodToText(lead.completed.paymentMethod)}
                    <br />
                    <strong>Comentario:</strong>{' '}
                    {lead.completed.comment || 'No asignado'}
                  </p>
                </section>
              )}
            </section>
          </section>
        </section>
      </article>
    );
  };

  return (
    <>
      <Modal size="lg" isOpen={modal} toggle={() => toggle('cancel')}>
        <ModalHeader toggle={() => toggle('cancel')}>
          Detalle del Lead
        </ModalHeader>
        <ModalBody>{renderLeadInfo()}</ModalBody>
        <ModalFooter>
          {isAdmin && leadDetailState?.data?.data && (
            <Button
              color="primary"
              className="mr-2"
              onClick={() => setShowEditForm(true)}
            >
              <i className="fa fa-edit mr-1" />
              Editar todo el contenido
            </Button>
          )}
          <Button color="secondary" onClick={() => toggle('cancel')}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      {showEditForm && leadDetailState?.data && (
        <LeadEditForm
          leadId={leadId}
          initialLead={leadDetailState.data}
          onSubmit={() => {
            setShowEditForm(false);
            dispatch(getLeadById(leadId));
            // No llamar onSubmit del padre: permite ver el detalle actualizado con PDFs sin recargar
          }}
          onCancel={() => setShowEditForm(false)}
        />
      )}
    </>
  );
}

export default LeadDetail;
